import _ from 'lodash'
import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import { UserError } from '@/utils/errors'
import BaseTool from '@glass-designer/tools/baseTool'
import EditingCutout from '@glass-designer/objects/editingCutout'
import EditingMoveHandle from '@glass-designer/objects/editingMoveHandle'
import EditingRotateHandle from '@glass-designer/objects/editingRotateHandle'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'
import CircleHole from '@glass-designer/model/holes/circleHole'
import OvalHole from '@glass-designer/model/holes/ovalHole'
import CustomHole from '@glass-designer/model/holes/customHole'
import DatumLink from '@glass-designer/model/datumLink'

import EditorClipper from '@glass-designer/utils/editorClipper'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'

import GlassDesignerTypes from '@glass-designer/types'


/**
 * Corner Add Cutout tool
 */
export default class AddCornerCutoutTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.addCornerCutout
    this.draggingCutoutActive = false
    this.holeObject = null
    this.selectedShape = null
    this.selectedSegment = null
    this.isSegmentStart = false
    this.isSegmentEnd = false
    this.cutoutEditDimRestoreWidth = 0
    this.cutoutEditDimRestoreHeight = 0

    this.centerCutoutInfoList = []

    this.rotateHandleDistance = 50
    this.snapMaxScreenDist = 25
    this.customCutoutConnectionDiff = new THREE.Vector2()

    this.datumLinks = []

    // Defaults
    this.cutoutInfo = {
      shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
      width: 100,
      height: 100,
      extendWidth: 0,
      cornerRadius: 5,
      radius: 50,
      inset: 0,
      cornerOffset: 0,
      angleIndex: 0,
      angleList: [],
      isFlipped: false,
      polished: false,
      cutoutMeasureType: GlassDesignerTypes.CutoutMeasurementTypes.edge,
      offsetHorizontalType: GlassDesignerTypes.HoleMeasurePointTypes.center,
      offsetVerticalType: GlassDesignerTypes.HoleMeasurePointTypes.middle
    }

    this.editingDimension = null

    this.cutoutEditDimRestorePosition = new THREE.Vector2()
    this.cutoutPosition = new THREE.Vector2()
    this.cutoutDirection = new THREE.Vector2()
    this.cutoutScale = new THREE.Vector2(1, 1)
    this.cutoutGrabOffset = new THREE.Vector2(0, 0)

    this.cutoutObject = new EditingCutout(editor)
    this.cutoutObject.setVisible(false)
    this.addChildObject(this.cutoutObject)

    this.rotateHandle = new EditingRotateHandle(editor, {
      size: 32,
      colour: '#808080',
      hoverColour: '#0D6A61'
    })
    this.rotateHandle.setVisible(false)
    this.rotateHandle.setObjectEventListener(this)
    this.addChildObject(this.rotateHandle)

    this.grabHandle = new EditingMoveHandle(editor, {
      size: 32,
      colour: '#808080',
      hoverColour: '#A00000',
      axisMoveAll: true
    })
    this.grabHandle.setVisible(false)
    this.grabHandle.setObjectEventListener(this)
    this.grabHandle.setRotation(0)
    this.addChildObject(this.grabHandle)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive() {
    super.onToolActive()

    // Reset
    this.centerCutoutInfoList = []
    this.draggingCutoutActive = false
    this.holeObject = null
    this.selectedShape = null
    this.selectedSegment = null
    this.cutoutObject.setVisible(false)
    this.rotateHandle.setVisible(false)
    this.grabHandle.setVisible(false)

    this.editor.setDimensionsEnabled([GlassDesignerTypes.DimensionTypes.editingCutoutDimension])
    this.editor.setShapeSelectionEnabled(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.centerCutoutInfoList.forEach(holeInfo => {
      this.editor.removeSceneObject(holeInfo.holeObject)
      holeInfo.holeObject.dispose()
    })
    this.centerCutoutInfoList = []

    if(this.holeObject) {
      this.holeObject.dispose()
      this.holeObject = null
    }

    this.selectedShape = null
    this.selectedSegment = null

    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)
    this.rotateHandle.setVisible(false)
    this.grabHandle.setVisible(false)

    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var point = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, point)) {
        this.selectedShape = this.editor.findClosestGlassShape(point, { ignoreStockLinked: true, ignoreSegmentListShapeUneditable: true })
        if(this.selectedShape) {

          // Find nearest segment on the shape to snap too
          // Adjust position to snap to the segment
          let closestSegmentIndex = this.selectedShape.calculateClosestSegmentToPoint(point)
          if(closestSegmentIndex != -1) {
            this.selectedSegment = this.selectedShape.getSegmentForIndex(closestSegmentIndex)
            let prevSegment = this.selectedShape.getSegmentForIndex(closestSegmentIndex-1)
            let nextSegment = this.selectedShape.getSegmentForIndex(closestSegmentIndex+1)

            let segmentDir = this.selectedSegment.getOutDirection()

            this.isSegmentStart = false
            this.isSegmentEnd = false

            // Find what is closest, start or end of segment
            if(this.selectedSegment.startPosition.distanceTo(point) < this.selectedSegment.endPosition.distanceTo(point)) {
              this.cutoutPosition.set(this.selectedSegment.startPosition.x, this.selectedSegment.startPosition.y)
              this.isSegmentStart = true

              this.cutoutInfo.angleIndex = 2
              let firstAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(prevSegment.getAngle()) + 90)
              let secondAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(this.selectedSegment.getAngle()) + 90)
              let midAngleDist = MathsUtils.calculateShortestAngleDistanceDegrees(firstAngle, secondAngle)
              let midAngle = MathsUtils.wrapAngleDegrees(firstAngle + midAngleDist / 2)

              this.cutoutInfo.angleList = [firstAngle, midAngle, secondAngle]

            } else {
              this.isSegmentEnd = true
              this.cutoutPosition.set(this.selectedSegment.endPosition.x, this.selectedSegment.endPosition.y)

              this.cutoutInfo.angleIndex = 0

              let firstAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(this.selectedSegment.getAngle()) + 90)
              let secondAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(nextSegment.getAngle()) + 90)
              let midAngleDist = MathsUtils.calculateShortestAngleDistanceDegrees(firstAngle, secondAngle)
              let midAngle = MathsUtils.wrapAngleDegrees(firstAngle + midAngleDist / 2)

              this.cutoutInfo.angleList = [firstAngle, midAngle, secondAngle]
            }

            this.cutoutDirection.set(-segmentDir.x, -segmentDir.y, 0)

            // For custom cutouts, figure out how the start/end of the cutout connects to the segments that make up the corner
            if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
              // Find closest vertex
              let segmentStartIndex = this.selectedShape.calculateClosestSegmentStartToPoint(point)
              if(segmentStartIndex != -1) {
                let segment = this.selectedShape.getSegmentForIndex(segmentStartIndex)
                let prevSegment = this.selectedShape.getSegmentForIndex(segmentStartIndex-1)

                this.cutoutPosition.set(segment.startPosition.x, segment.startPosition.y)
                this.cutoutDirection.set(0, 1, 0)
              }
            }

            this._updateCutoutObjectPosition()

            // Calculate the 2 datum reference objects in the shape
            // Each edge of the segment
            if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
              this._calculateCutoutDatumLinks(this.selectedShape, this.selectedSegment, this.cutoutPosition)
              this.cutoutObject.setDatumLinks(this.datumLinks)
            }
          }

          this.cutoutObject.setVisible(true)

          if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
            // Update the dimensions for the cutout to the shape
            this.editor.onToolEditingUpdated(this.selectedShape, [this.cutoutObject])
          }

          this.editor.flagRenderRequired()
        }
      }
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      this.draggingCutoutActive = false

      // If no nearby shape available, stop the tool
      if(!this.selectedShape) {
        this.editor.component.errorMessage('No valid editable panel found near the cutout')
        this.editor.cancelActiveTool()
        return
      }

      // Update shape based on guidelines for selected shape
      this._applyCutoutGuidelines()

      // Setup grab handles for custom cutouts
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
        this.grabHandle.setVisible(true)
        this.rotateHandle.setVisible(true)
      }
      // Rotate handle for rectangle
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
        this.rotateHandle.setVisible(true)
      }

      this.editor.flagRenderRequired()

      this.displayToolSettingsPopover()
    }
  }


  /**
   * Display the tool settings popover for the add cutout tool
   */
  displayToolSettingsPopover() {

    // Display the editing cutout popover
    this.editor.component.displayCornerCutoutToolPopover({
      title: 'Add Corner Cutout Tool',
      shapeType: this.cutoutInfo.shapeType,
      radius: this.cutoutInfo.radius,
      cornerRadius: this.cutoutInfo.cornerRadius,
      inset: this.cutoutInfo.inset,
      width: this.cutoutInfo.width,
      height: this.cutoutInfo.height,
      extendWidth: this.cutoutInfo.extendWidth,
      cutoutMeasureType: this.cutoutInfo.cutoutMeasureType,
      isFlipped: this.cutoutInfo.isFlipped,
      polished: this.cutoutInfo.polished,
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      applyCallback: (ctx, info) => {
        this._updateCutout(info)

        // Apply cutout to shape
        this._applyCornerCutout()
      },
      updateCallback: (ctx, info) => {
        this._updateCutout(info)

        ctx.editor.render()
      }
    })
  }


  /**
   * A library item was dragged into the editor while this tool was active
   */
  libraryItemDraggedToEditor(itemInfo) {
    this.draggingCutoutActive = true
    this.holeObject = null
    this.cutoutScale.set(1, 1)

    var cutoutData = {}
    var centerCutoutList = []

    if(itemInfo.itemData) {
      if(_.isArray(itemInfo.itemData.cutoutList) && itemInfo.itemData.cutoutList.length > 0) 
        cutoutData = itemInfo.itemData.cutoutList[0].cutoutData
      if(_.isArray(itemInfo.itemData.centerCutoutList))
        centerCutoutList = itemInfo.itemData.centerCutoutList
    }
    
    // Rectangle / Circle / Custom
    this.cutoutInfo = {
      shapeType: cutoutData.shapeType,
      width: parseInt(cutoutData.width || 100),
      height: parseInt(cutoutData.height || 50),
      extendWidth: 0,
      cutoutMeasureType: GlassDesignerTypes.CutoutMeasurementTypes.edge,
      cornerRadius: parseFloat(cutoutData.cornerRadius || 5),
      radius: parseFloat(cutoutData.radius || 10),
      inset: parseInt(cutoutData.inset || 0),
      isFlipped: false,
      polished: false,
      offsetHorizontalType: GlassDesignerTypes.HoleMeasurePointTypes.center,
      offsetVerticalType: GlassDesignerTypes.HoleMeasurePointTypes.middle
    }

    if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      this.cutoutInfo.offsetVerticalType = GlassDesignerTypes.HoleMeasurePointTypes.top

      this.holeObject = new RectangleHole(this.editor, { width: this.cutoutInfo.width, height: this.cutoutInfo.height, cornerRadius: this.cutoutInfo.cornerRadius })

      // Clear top corners
      this.holeObject.cornerRadiusTL = 0
      this.holeObject.cornerRadiusTR = 0
      this.holeObject.rebuildHoleShape()

    } else if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.holeObject = new CircleHole(this.editor, { radius: this.cutoutInfo.radius })

      this.cutoutInfo.offsetVerticalType = GlassDesignerTypes.HoleMeasurePointTypes.middle

    } else if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
      this.holeObject = new CustomHole(this.editor, {
        segmentList: cutoutData.segmentList,
        isOpenShape: true,
        isCornerShape: true
      })
    }
  
    this.holeObject.setAnchorInfo(this.cutoutInfo.offsetHorizontalType, this.cutoutInfo.offsetVerticalType)
    this.holeObject.setVisible(false)
    this.cutoutObject.buildFromHole(this.holeObject)
    this.cutoutObject.setVisible(false)

    // Add in the optional center cutouts associated with the shape cutout
    this.centerCutoutInfoList = []
    this.buildCenterCutoutList(centerCutoutList)

    this._recalculateCustomCutoutConnections()

    this.editor.flagRenderRequired()
  }


  /**
   * Build the list of center cutouts associated with the corner cutout
   */
  buildCenterCutoutList(centerCutoutList) {
    // Support list of hole objects
    centerCutoutList.forEach(cutoutInfo => {
      var cutoutData = cutoutInfo.cutoutData
      if(!cutoutData)
        return

      var holeObject = null
      
      // Rectangle / Circle
      if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
         holeObject = new RectangleHole(this.editor, {
          width: parseInt(cutoutData.width || 100),
          height: parseInt(cutoutData.height || 50),
          cornerRadiusTL: parseFloat(cutoutData.cornerRadiusTL || 5),
          cornerRadiusTR: parseFloat(cutoutData.cornerRadiusTR || 5),
          cornerRadiusBR: parseFloat(cutoutData.cornerRadiusBR || 5),
          cornerRadiusBL: parseFloat(cutoutData.cornerRadiusBL || 5),
          angle: parseInt(cutoutData.angle || 0),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
        holeObject = new CircleHole(this.editor, {
          radius: parseFloat(cutoutData.radius || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished,
          isCountersunk: !!cutoutData.isCountersunk,
          countersunkRadius: parseFloat(cutoutData.countersunkRadius || 0)
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
        holeObject = new OvalHole(this.editor, {
          width: parseInt(cutoutData.width || 5),
          height: parseInt(cutoutData.height || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else {
        // Unknown cutout type
        return
      }

      if(holeObject) {
        holeObject.setVisible(false)
        holeObject.setAnchorInfo(cutoutData.measureToHorizontal, cutoutData.measureToVertical)

        var offset = new THREE.Vector2(0, 0)
        if(cutoutInfo.offset)
          offset.set(parseFloat(cutoutInfo.offset.x || 0), parseFloat(cutoutInfo.offset.y || 0))

        this.centerCutoutInfoList.push({
          holeObject: holeObject,
          offset: offset
        })

        this.cutoutObject.addHoleWithOffset(holeObject, offset)
      }
    })
  }


  /**
   * Update the cutout from the popover info
   */
  _updateCutout(updateData) {
    this.cutoutInfo.width = parseInt(updateData.width)
    this.cutoutInfo.height = parseInt(updateData.height)
    this.cutoutInfo.extendWidth = parseInt(updateData.extendWidth)
    this.cutoutInfo.cornerRadius = parseFloat(updateData.cornerRadius)
    this.cutoutInfo.radius = parseInt(updateData.radius)
    this.cutoutInfo.inset = parseInt(updateData.inset)
    this.cutoutInfo.cutoutMeasureType = updateData.cutoutMeasureType
    this.cutoutInfo.isFlipped = !!updateData.isFlipped
    this.cutoutInfo.polished = !!updateData.polished

    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      this.holeObject.width = this.cutoutInfo.width + this.cutoutInfo.extendWidth
      this.holeObject.height = this.cutoutInfo.height

      this.holeObject.cornerRadiusBL = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusBR = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusTL = 0
      this.holeObject.cornerRadiusTR = 0
      this.holeObject.rebuildHoleShape()
    }

    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.holeObject.radius = this.cutoutInfo.radius
      this.holeObject.rebuildHoleShape()
    }

    // Update position to account for inset changes
    this._updateCutoutObjectPosition()
    this._recalculateCustomCutoutConnections()

    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      this._calculateCutoutDatumLinks(this.selectedShape, this.selectedSegment, this.cutoutPosition)
      this.cutoutObject.setDatumLinks(this.datumLinks)

      // Recalc dimensions
      this.editor.onToolEditingUpdated(this.selectedShape, [this.cutoutObject])
    }
  }


  /**
   * Update the cutout object position/rotate
   */
  _updateCutoutObjectPosition() {
    let angle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.cutoutDirection)) - 90)

    var cutoutPos = new THREE.Vector2(this.cutoutPosition.x, this.cutoutPosition.y)

    // Rectangle: Adjust so cutout is to the edge for either segment snaps (but not middle)
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      let offsetHorzTypes = [GlassDesignerTypes.HoleMeasurePointTypes.right, GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.left]
      this.cutoutInfo.offsetHorizontalType = offsetHorzTypes[this.cutoutInfo.angleIndex]

      let rightDir = new THREE.Vector2(this.cutoutDirection.y, -this.cutoutDirection.x)
      if(this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.left)
        cutoutPos.addScaledVector(rightDir, -this.cutoutInfo.extendWidth)
      else if(this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.right)
        cutoutPos.addScaledVector(rightDir, this.cutoutInfo.extendWidth)
    }

    // Add inset for circle corner cutouts
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      cutoutPos.addScaledVector(this.cutoutDirection, this.cutoutInfo.inset)

      // Don't rotate circles. No need
      angle = 0
    }

    this.cutoutScale.x = this.cutoutInfo.isFlipped ? -1 : 1

    // Rebuild if rectangle/circle
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle || this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.holeObject.setAnchorInfo(this.cutoutInfo.offsetHorizontalType, this.cutoutInfo.offsetVerticalType)
      this.cutoutObject.buildFromHole(this.holeObject)
    }

    this.cutoutObject.setPosition(cutoutPos)
    this.cutoutObject.setRotation(angle)
    this.cutoutObject.setScale(this.cutoutScale)

    let rotateHandleAngle = angle + (this.cutoutInfo.isFlipped ? 180 : 0)
    let rotateHandlePos = cutoutPos.clone().addScaledVector(MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(rotateHandleAngle)), this.rotateHandleDistance)

    this.cutoutGrabOffset = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(angle + 90)).multiplyScalar(15)

    this.grabHandle.setPosition(cutoutPos.add(this.cutoutGrabOffset))
    this.rotateHandle.setPosition(rotateHandlePos)
    this.rotateHandle.setRotation(rotateHandleAngle + 90)
  }


  /**
   * Apply the corner cutout using the current position/angle/hole details
   * Applies to the last set shape while editing
   */
  _applyCornerCutout() {
    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)

    try {
      var resultSegmentList = []
      let groupStartPosition = null
      let groupEndPosition = null

      let subjectSegmentList = this.selectedShape.segmentList
      var clipSegmentList = this.cutoutObject.generateSegmentList()

      // For custom, we don't using clipping, we just use the entry/exist points of the shape & insert it into the glass panel
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {

        let clipResult = MathsUtils.insertCustomOpenShapeToShape(subjectSegmentList, clipSegmentList)

        resultSegmentList = clipResult.segmentList
        groupStartPosition = clipResult.startPosition.clone()
        groupEndPosition = clipResult.endPosition.clone()
      }


      // Apply an offset for the rectangle cutout to not be coplanar with the glass shape
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
        let rectangleOffset = 4
        let rightDir = new THREE.Vector2(this.cutoutDirection.y, -this.cutoutDirection.x)

        this.holeObject.height = this.cutoutInfo.height + rectangleOffset / 2
        if(this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.left || this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.right) {
          this.holeObject.width = this.cutoutInfo.width + rectangleOffset + this.cutoutInfo.extendWidth
        }

        this.holeObject.rebuildHoleShape()
        this.holeObject.setAnchorInfo(this.cutoutInfo.offsetHorizontalType, this.cutoutInfo.offsetVerticalType)

        var cutoutPos = new THREE.Vector2(this.cutoutPosition.x, this.cutoutPosition.y)
        cutoutPos.addScaledVector(this.cutoutDirection, -rectangleOffset / 2)

        // Right/Left offset
        if(this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.left)
          cutoutPos.addScaledVector(rightDir, -rectangleOffset - this.cutoutInfo.extendWidth)
        else if(this.cutoutInfo.offsetHorizontalType == GlassDesignerTypes.HoleMeasurePointTypes.right)
          cutoutPos.addScaledVector(rightDir, rectangleOffset + this.cutoutInfo.extendWidth)
        
        this.cutoutObject.setPosition(cutoutPos)
        this.cutoutObject.updateFromHole(this.holeObject)

        clipSegmentList = this.cutoutObject.generateSegmentList()
      }

      // Run the clipping tool for non-custom shapes
      if(this.cutoutInfo.shapeType != GlassDesignerTypes.LibraryItemShapeTypes.custom) {

        let clipper = new EditorClipper()
        let clipResult = clipper.performSegmentListClippingOperation(subjectSegmentList, clipSegmentList, false)
        resultSegmentList = clipResult.segmentList

        // Setup group info for cutout groups
        groupStartPosition = clipResult.startPosition.clone()
        groupEndPosition = clipResult.endPosition.clone()
      }

      if(resultSegmentList.length == 0) {
        throw new EditorUserError('Cutting shape does not intersect glass')
      }

      // Reset dimensions for editing
      this.editor.dimensionHelper.resetEditingDimensions()

      this.editor.startUndoGroup('Adding Corner Cutout')
      this.editor.addUndoShape(this.selectedShape)
      this.selectedShape.segmentList = resultSegmentList

      // Setup matrix for the center cutouts
      let angle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.cutoutDirection)) - 90)

      let cutoutMatrix = new THREE.Matrix4()
      let objAngles = new THREE.Euler(0, 0, MathsUtils.degreesToRadians(angle))
      cutoutMatrix.makeRotationFromEuler(objAngles)
      cutoutMatrix.scale(new THREE.Vector3(this.cutoutScale.x, this.cutoutScale.y, 1.0))
      cutoutMatrix.setPosition(new THREE.Vector3(this.cutoutPosition.x, this.cutoutPosition.y, 0))
      

      // Add in the center cutouts
      this.centerCutoutInfoList.forEach(holeInfo => {
        // Calculate datum links for each hole with offset adjusted
        var holeDatumLinks = [
          new DatumLink(),
          new DatumLink()
        ]

        // Need to transform hole position by the cutout object correctly
        // Also flip angle if rectangle if scale flipped
        let holePosition = new THREE.Vector3(holeInfo.offset.x, holeInfo.offset.y, 0)
        holePosition = holePosition.applyMatrix4(cutoutMatrix)
        EditorDatumUtils.calculateClosestDatumLinks(this.selectedShape, holeDatumLinks, new THREE.Vector2(holePosition.x, holePosition.y), { ignoreAllHoles: false })

        // Run checks here. If outside shape, flag error
        if(!this.selectedShape.isPointInsideShape(holePosition))
          throw new EditorUserError('Center cutout is outside of glass panel')

        // Rotate / flip center cutout
        holeInfo.holeObject.angle += angle
        if(this.cutoutScale.x < 0)
          holeInfo.holeObject.angle = 360 - holeInfo.holeObject.angle
        holeInfo.holeObject.angle = MathsUtils.wrapAngleDegrees(holeInfo.holeObject.angle)

        // Datum links to segments / other holes
        holeInfo.holeObject.setVisible(true)
        holeInfo.holeObject.setDatumLinks(holeDatumLinks)
        this.selectedShape.addCenterCutout(holeInfo.holeObject)
      })
      // Clear cutout info list, don't release it as it's owned by the shape now
      this.centerCutoutInfoList = []

      // Update cutout groups in the shape
      if(groupStartPosition && groupEndPosition) {
        this.selectedShape.updateCutoutGroup(groupStartPosition, groupEndPosition, {
          cutoutType: GlassDesignerTypes.CutoutTypes.corner,
          cutoutMeasurementType: this.cutoutInfo.cutoutMeasureType,
          polished: this.cutoutInfo.polished
        })
      }
  
      this.editor.closeCurrentUndoGroup()
      this.selectedShape.segmentListUpdated()
      this.selectedShape.updateSceneObject()
      this.editor.onShapeUpdated(this.selectedShape)


      this.closeTool()
    } catch(err) {
      this.editor.cancelCurrentUndoGroup()

      // Display error (eg, hole outside of shape or whatever)
      if(err instanceof UserError || err instanceof EditorUserError)
        this.editor.component.errorMessage('Error placing corner cutout', err.message)

      console.error(err)

      // Close & cleanup
      this.closeTool()
    }    
  }


  /**
   * Apply the minimum corner radius info based on guidelines for the selected shape to a standard rectangle/circle cutout
   * Not done for custom cutouts as they are to spec. They'll just show warnings if invalid
   */
  _applyCutoutGuidelines() {
    if(!this.selectedShape)
      return
  
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(this.selectedShape.getGlassType(), this.selectedShape.getThickness(), 25)    // 25% for 1 corner out of 4

      this.cutoutInfo.cornerRadius = Math.max(this.cutoutInfo.cornerRadius, minRadius)
      this.holeObject.cornerRadiusBL = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusBR = this.cutoutInfo.cornerRadius
      this.holeObject.rebuildHoleShape()
      this.cutoutObject.buildFromHole(this.holeObject)
    }
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(this.selectedShape.getGlassType(), this.selectedShape.getThickness(), 50)    // 50%, assumes circle is half on/off the edge

      this.cutoutInfo.radius = Math.max(this.cutoutInfo.radius, minRadius)
      this.holeObject.radius = this.cutoutInfo.radius
      this.holeObject.rebuildHoleShape()
      this.cutoutObject.buildFromHole(this.holeObject)
    }
  }


  /**
   * Recalculate the custom cutout connection points
   * Done after rotation/flip/mirror
   */
  _recalculateCustomCutoutConnections() {

    // Generate the segments with full transform applied
    // It's assumed the start position in the 1st segment is the origin point
    let segments = this.cutoutObject.generateSegmentList()
    if(segments.length == 0)
      return

    let start = segments[0].startPosition
    let end = segments[segments.length-1].endPosition

    // Difference. NOT direction, don't normalize
    this.customCutoutConnectionDiff.set(end.x - start.x, end.y - start.y)
  }


  /**
   * Move a custom cutout to a position & snap to the selected shape if the start/end connections of the cutout hits the shape
   * Note: Does not rotate the cutout, only attempts to snap if possible
   */
  _snapCustomCutoutToShapePosition(targetPosition) {
    if(!this.selectedShape)
      return

    var renderScale = 1.0 / this.editor.camera.zoom
    var maxSnapDist = this.snapMaxScreenDist * renderScale

    // Snap position to nearest point on the shape
    let closestPointInfo = this.selectedShape.calculateClosestEdgePoint(targetPosition, {
      ignoreDatumLines: true,
      ignoreSplitLines: true,
      ignoreHoles: true
    })
    if(!closestPointInfo)
      return
    
    this.cutoutPosition.set(closestPointInfo.point.x, closestPointInfo.point.y)
  
    // THEN, if snap possible for other connection, snap as well
    let endConnPosition = new THREE.Vector2(closestPointInfo.point.x + this.customCutoutConnectionDiff.x, closestPointInfo.point.y + this.customCutoutConnectionDiff.y)
    let closestEndPointInfo = this.selectedShape.calculateClosestEdgePoint(endConnPosition, {
      ignoreDatumLines: true,
      ignoreSplitLines: true,
      ignoreHoles: true
    })
    if(closestEndPointInfo) {
      if(closestEndPointInfo.distance <= maxSnapDist) {

        // Move the cutout position to account for the connection
        this.cutoutPosition.set(closestPointInfo.point.x + (closestEndPointInfo.point.x - endConnPosition.x), closestPointInfo.point.y + (closestEndPointInfo.point.y - endConnPosition.y))
      }      
    }

    this._updateCutoutObjectPosition()
  }


  /**
   * Receive an object event from objects we're listening too
   */
   onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectCaptured) {
      if(object == this.grabHandle) {
        if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
          this._updateCutoutObjectPosition()
          this._recalculateCustomCutoutConnections()
        }
      }
    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == this.rotateHandle) {

        // Rotate around
        var angleDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.rotateHandle.position.clone().sub(this.cutoutPosition))))
        if(this.cutoutInfo.isFlipped)
          angleDegrees = MathsUtils.wrapAngleDegrees(angleDegrees + 180)
        
        // Snap to nearest 45
        if(this.editor.getOptionSnapEnabled()) {
          let snapAngle = 45
          angleDegrees = Math.round(angleDegrees / snapAngle) * snapAngle
        }

        // If a rectangle, limit to the available angle list
        if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
          let closestAngleIndex = -1
          let closestAngle = 0

          angleDegrees += 90

          this.cutoutInfo.angleList.forEach((listAngle, index) => {
            let dist = MathsUtils.calculateShortestAngleDistanceDegrees(listAngle, angleDegrees)
            if(dist < closestAngle || closestAngleIndex == -1) {
              closestAngle = dist
              closestAngleIndex = index
            }
          })
          if(closestAngleIndex != -1) {
            angleDegrees = this.cutoutInfo.angleList[closestAngleIndex] - 90
            this.cutoutInfo.angleIndex = closestAngleIndex
          }
        }


        let dir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(angleDegrees + 90))
        this.cutoutDirection.set(dir.x, dir.y)

        this._updateCutoutObjectPosition()

        if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
          this._calculateCutoutDatumLinks(this.selectedShape, this.selectedSegment, this.cutoutPosition)
          this.cutoutObject.setDatumLinks(this.datumLinks)
        }
        if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
          // Update the dimensions for the cutout to the shape
          this.editor.onToolEditingUpdated(this.selectedShape, [this.cutoutObject])
        }

        this.editor.flagRenderRequired()
      }
      if(object == this.grabHandle) {
        // Snap points to shape
        this._snapCustomCutoutToShapePosition(this.grabHandle.position.clone().sub(this.cutoutGrabOffset))

        this.editor.flagRenderRequired()
      }
    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectReleased) {
      if(object == this.rotateHandle) {
        this._updateCutoutObjectPosition()
        this.editor.flagRenderRequired()
      }
      if(object == this.grabHandle) {
        this._updateCutoutObjectPosition()
        this.editor.flagRenderRequired()
      }
    }
  }


  /**
   * Calculate the datum links to point to a cutout along a segment from either of the segments ends 
   * Note: Only handles rectangle corner cutouts
   * Updates datumLinks array
   */
   _calculateCutoutDatumLinks(shape, segment, position) {
    if(this.datumLinks.length == 0) {
      this.datumLinks.push(new DatumLink())
      this.datumLinks.push(new DatumLink())
    }

    var cutoutEdgePos1 = null
    var cutoutEdgePos2 = null

    let segDir = segment.getDirection()
    var cutoutWidth = this.isSegmentEnd ? this.cutoutInfo.width : this.cutoutInfo.height

    // Rotate around if angle aligned with height
    if(this.cutoutInfo.angleIndex == 2)
      cutoutWidth = this.isSegmentEnd ? this.cutoutInfo.height : this.cutoutInfo.width

    switch(this.cutoutInfo.cutoutMeasureType) {
      case GlassDesignerTypes.CutoutMeasurementTypes.center:
        cutoutEdgePos1 = segment.projectPointOnSegment(position, false)

        cutoutEdgePos2 = cutoutEdgePos1.clone()
        cutoutEdgePos1.addScaledVector(segDir, -cutoutWidth/2)
        cutoutEdgePos2.addScaledVector(segDir, cutoutWidth/2)
        break

      case GlassDesignerTypes.CutoutMeasurementTypes.edge:
        cutoutEdgePos1 = segment.projectPointOnSegment(position, false)

        if(this.isSegmentStart) {
          cutoutEdgePos1.addScaledVector(segDir, cutoutWidth)
          cutoutEdgePos2 = cutoutEdgePos1.clone()
        } else {
          cutoutEdgePos2 = cutoutEdgePos1.clone().addScaledVector(segDir, -cutoutWidth)
          cutoutEdgePos1 = cutoutEdgePos2.clone()
        }
        break
    }
    
    let segStartDistanceAlong = segment.startPosition.distanceTo(cutoutEdgePos1)
    let segEndDistanceAlong = segment.endPosition.distanceTo(cutoutEdgePos2)

    this.datumLinks[0].clear()
    this.datumLinks[1].clear()

    // Ignore if the angle is not aligned with the segment
    if(this.cutoutInfo.angleIndex == 1)
      return

    // Update datum links    
    this.datumLinks[0].update({
      sourceShapeId: shape.id,
      sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegmentStart,
      sourceOffsetDistance: segStartDistanceAlong,
      sourceOffsetDirection: 1,
      segmentId: segment.id
    })

    this.datumLinks[1].update({
      sourceShapeId: shape.id,
      sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegmentEnd,
      sourceOffsetDistance: segEndDistanceAlong,
      sourceOffsetDirection: 1,
      segmentId: segment.id
    })
  }


  // A dimension used while editing in this tool was clicked
  editingDimensionClicked(dimension) {
    if(dimension.dimType != GlassDesignerTypes.DimensionTypes.editingCutoutDimension)
      return

    this.editingDatumLinkId = dimension.linkData.datumLinkId
    this.cutoutEditDimRestoreWidth = this.cutoutInfo.width
    this.cutoutEditDimRestoreHeight = this.cutoutInfo.height
    
    // Fill in options
    var popoverOptions = {
      value: dimension.getTotalLength(),
      displayUserFixedField: false,
      allowPlusMinus: false,
      title: 'Length',
      addon: 'mm',
      context: this,
      updateCallback: this.updateDimensionEdit,
      cancelCallback: this.cancelDimensionEdit,
      applyCallback: this.applyDimensionEdit
    }

    this.editor.component.displayNumberEntryPopover(popoverOptions)
  }

  updateDimensionEdit(context, options) {
    context._updateEditingCutoutFromLinearDimensionEdit(context.selectedShape, context.editingDatumLinkId, options.value)
  }
  applyDimensionEdit(context, options) {
    context._updateEditingCutoutFromLinearDimensionEdit(context.selectedShape, context.editingDatumLinkId, options.value)
  }

  // Update the editing cutout from a linear dimension editing
  _updateEditingCutoutFromLinearDimensionEdit(shape, datumLinkId, newLength) {
    let datumLink = this.datumLinks.find(datum => datum.id == datumLinkId)
    if(!datumLink)
      return

    let segment = shape.getSegmentById(datumLink.segmentId)
    if(!segment)
      return

    let cutoutWidth = this.cutoutInfo.width
    let cutoutHeight = this.cutoutInfo.height
    let flipAxis = (this.cutoutInfo.angleIndex == 0 && this.isSegmentStart) || (this.cutoutInfo.angleIndex == 2 && this.isSegmentEnd)
    
    if(datumLink.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart) {
      switch(this.cutoutInfo.cutoutMeasureType) {
        case GlassDesignerTypes.CutoutMeasurementTypes.center:
          if(flipAxis)
            cutoutHeight = (this.isSegmentEnd ? (segment.getLength() - newLength) : newLength) * 2
          else
            cutoutWidth = (this.isSegmentEnd ? (segment.getLength() - newLength) : newLength) * 2
          break
        case GlassDesignerTypes.CutoutMeasurementTypes.edge:
          if(flipAxis)
            cutoutHeight = this.isSegmentEnd ? (segment.getLength() - newLength) : newLength
          else
            cutoutWidth = this.isSegmentEnd ? (segment.getLength() - newLength) : newLength
          break
      }
    } else {
      switch(this.cutoutInfo.cutoutMeasureType) {
        case GlassDesignerTypes.CutoutMeasurementTypes.center:
          if(flipAxis)
            cutoutHeight = (this.isSegmentStart ? (segment.getLength() - newLength) : newLength) * 2
          else
            cutoutWidth = (this.isSegmentStart ? (segment.getLength() - newLength) : newLength) * 2
          break
        case GlassDesignerTypes.CutoutMeasurementTypes.edge:
          if(flipAxis)
            cutoutHeight = this.isSegmentEnd ? newLength : (segment.getLength() - newLength)
          else
            cutoutWidth = this.isSegmentEnd ? newLength : (segment.getLength() - newLength)
          break
      }
    }

    this.cutoutInfo.width = cutoutWidth
    this.cutoutInfo.height = cutoutHeight

    // Update cutout & datum links
    this._updateCutout(this.cutoutInfo)

    this._updateCutoutObjectPosition()
    this._calculateCutoutDatumLinks(this.selectedShape, segment, this.cutoutPosition)
    this.cutoutObject.setDatumLinks(this.datumLinks)

    // Recalc dimensions
    this.editor.onToolEditingUpdated(shape, [this.cutoutObject])

    this.editor.render()
  }

  cancelDimensionEdit(context, userCancelled) {
    // Restore from previous value
    if(userCancelled) {
      context.cutoutInfo.width = context.cutoutEditDimRestoreWidth
      context.cutoutInfo.height = context.cutoutEditDimRestoreHeight

      // Update cutout & datum links
      context._updateCutout(context.cutoutInfo)
      context._updateCutoutObjectPosition()
      context._calculateCutoutDatumLinks(context.selectedShape, context.selectedSegment, context.cutoutPosition)
      context.cutoutObject.setDatumLinks(context.datumLinks)
    }

    // Recalc dimensions
    context.editor.onToolEditingUpdated(context.selectedShape, [context.cutoutObject])

    context.editingDatumLinkId = null
    context.displayToolSettingsPopover()
  }
}